import {
  useEffect,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
} from 'react';

type Axis = 'horizontal' | 'vertical';

interface PaneResizeOptions {
  // 'horizontal' pins a pane width off the container's right edge (default);
  // 'vertical' pins a pane height off the container's bottom edge.
  axis?: Axis;
  // Minimum size (px) of the pinned pane.
  minSize?: number;
  // Minimum size (px) kept for the rest of the container.
  minOther?: number;
}

// Draggable split shared by the structuring and entity-page reconcile
// tearsheets: null = CSS default size; a number pins the resized pane's size
// (px) after the user drags the divider. `axis` selects a horizontal
// (candidates | preview, or the side doc pane) or vertical (the stacked doc
// pane) split.
export function usePaneResize(options: PaneResizeOptions = {}) {
  const { axis = 'horizontal', minSize = 320, minOther = 360 } = options;
  const [previewWidth, setPreviewWidth] = useState<number | null>(null);
  const columnsRef = useRef<HTMLDivElement>(null);

  // Holds the teardown for an in-progress drag so an unmount mid-drag doesn't
  // leak the document listeners (and setState on an unmounted component).
  const endResizeRef = useRef<(() => void) | null>(null);
  useEffect(() => () => endResizeRef.current?.(), []);

  const startResize = (e: ReactMouseEvent) => {
    e.preventDefault();
    const container = columnsRef.current;
    if (!container) return;
    const cursor = axis === 'vertical' ? 'row-resize' : 'col-resize';

    // The doc-image preview is an <iframe>. Without this overlay, once the
    // cursor crosses the iframe during a drag the mousemove/mouseup fire inside
    // the iframe's document — the listeners below never see the release, so the
    // drag never ends. A transparent, top-most overlay keeps every pointer
    // event in the parent document until the drag completes.
    const overlay = document.createElement('div');
    overlay.style.cssText = `position:fixed;inset:0;z-index:10000;cursor:${cursor};`;
    document.body.appendChild(overlay);

    const onMove = (ev: MouseEvent) => {
      const rect = container.getBoundingClientRect();
      // Pinned pane spans from the cursor to the container's far edge, clamped
      // so neither side collapses.
      const size =
        axis === 'vertical'
          ? Math.min(
              Math.max(rect.bottom - ev.clientY, minSize),
              rect.height - minOther
            )
          : Math.min(
              Math.max(rect.right - ev.clientX, minSize),
              rect.width - minOther
            );
      setPreviewWidth(size);
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      overlay.remove();
      document.body.style.userSelect = '';
      endResizeRef.current = null;
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    endResizeRef.current = onUp;
  };

  return { previewWidth, columnsRef, startResize };
}
